import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { UtilityService } from '../../services/utility.service';
import { LocalCacheManager } from '../managers/local-cache.manager';
import { ActiveControllerInfo } from '../models/active-controller-info.model';
import { DailyWeatherModel } from '../models/daily-weather.model';
import { ToroLocationApanSetup } from '../models/toro-location-apan-setup.model';
import { ToroLocationDetailed } from '../models/toro-location-detailed.model';
import { ToroLocation } from '../models/toro-location.model';
import { ApiUrlService } from './api-url.service';
import { ApiService } from './api.service';

@Injectable()
export class LocationApiService extends ApiService {
    constructor(protected http: HttpClient, protected apiUrlService: ApiUrlService, protected localCacheManager: LocalCacheManager) {
        super(http, localCacheManager);
    }

    stopLocationIrrigation(locationId: number): Observable<{ success: boolean, error: string }> {
        return this.apiRequest<{ success: boolean, error: string }>(this.apiUrlService.getStopIrrigationForLocationUrl(locationId), this.HttpMethod.Put)
            .pipe(
                map(response => ({ success: response['Success'], error: response['Error'] }))
            );

    }

    getLocationCoordinates(address: string): Observable<any> {
        address = address.replace(' ', '%20');
        return this.apiRequestWithoutAuthorization(this.apiUrlService.getGoogleGeocodeUrl(address), this.HttpMethod.Get);
    }

    getLocationDetails(locationId: number): Observable<ToroLocationDetailed> {
        return this.apiRequest(this.apiUrlService.getLocationDetailsUrl(locationId), this.HttpMethod.Get);
    }

    getGatewayStatus(locationId: number): Observable<ToroLocationDetailed> {
        return this.apiRequest(this.apiUrlService.getGatewayStatusUrl(locationId), this.HttpMethod.Get);
    }
    setDefaultThemeForScenes(locationId: number): Observable<any> {
        return this.apiRequest(this.apiUrlService.setDefaultThemeForScenesUrl(locationId), this.HttpMethod.Get);
    }

    createLocation(accountId: number, location: ToroLocation): Observable<ToroLocation> {
        const area: ToroLocationApanSetup = {
            Pin: UtilityService.generateUniqueLightingPin(),
            DawnOffset: 0,
            DuskOffset: 0,
            UseDaylightSavings: true
        };

        const obj = {
            'Location': {
                'Name': location.Name,
                'Description': location.Description,
                'Latitude': location.Latitude,
                'Longitude': location.Longitude,
                'UIZoomLevel': location.UIZoomLevel,
                'ImageAssetId': location.ImageAssetId
            },
            'Area': area
        };
        return this.apiRequest(this.apiUrlService.getCreateLocationUrl(accountId), this.HttpMethod.Post, JSON.stringify(obj));
    }

    getServerAPIVersion() {
        return this.apiRequest(this.apiUrlService.getServerAPIVersionUrl(), this.HttpMethod.Get);
    }

    updateLocation(accountId: number, location: ToroLocation): Observable<ToroLocation> {
        const obj = {
            'Location': {
                'Name': location.Name,
                'Description': location.Description,
                'Latitude': location.Latitude,
                'Longitude': location.Longitude,
                'UIZoomLevel': location.UIZoomLevel,
                'ImageAssetId': location.ImageAssetId
            },
        };
        return this.apiRequest(this.apiUrlService.getUpdateLocationUrl(accountId), this.HttpMethod.Put, JSON.stringify(obj));
    }

    updateLocationWithApan(accountId: number, location: ToroLocation, area: ToroLocationApanSetup): Observable<ToroLocation> {
        const obj = {
            'Location': {
                'Name': location.Name,
                'Description': location.Description,
                'Latitude': location.Latitude,
                'Longitude': location.Longitude,
                'UIZoomLevel': location.UIZoomLevel,
                'ImageAssetId': location.ImageAssetId
            },
            'Area': area
        };
        return this.apiRequest(this.apiUrlService.getUpdateLocationUrl(accountId), this.HttpMethod.Put, JSON.stringify(obj));
    }

    deleteLocation(locationId: number): Observable<any> {
        return this.apiRequest(this.apiUrlService.getDeleteLocationUrl(locationId), this.HttpMethod.Delete);
    }

    getLocationWeatherForecast(latitude: number, longitude: number): Observable<{ [date: string]: DailyWeatherModel }> {
        return this.apiRequest(this.apiUrlService.getWeatherForecastUrl(latitude, longitude), this.HttpMethod.Get)
            .pipe(
                map(response => {
                    return response[Object.keys(response)[0]];
                })
            );
    }

    getActiveControllers(locationId: number): Observable<ActiveControllerInfo[]> {
        return this.apiRequest(this.apiUrlService.getActiveControllers(locationId), this.HttpMethod.Get)
            .pipe(
                map((response: ActiveControllerInfo[]) => {
                    const activeControllers = [];
                    response.forEach(activeController => {
                        activeControllers.push(new ActiveControllerInfo(activeController));
                    });
                    return activeControllers;
                })
            );
    }

}
